import formReducer, { formHelperMethodFactory } from 'reducers/FormReducer/FormReducer'
import {
  BaseValidationField,
  createFormField,
  FormReducerMultiTextField,
  FormReducerSelectField,
  FormReducerSingleTextField,
  PickByType,
  ValidationObject,
  withValidationFactory,
} from 'reducers/FormReducer/utils'
import type { ProductsDetailDataV5 } from 'utils/utils.d'

export interface EditProductFormReducerState {
  reference_name: FormReducerSingleTextField
  product_id: FormReducerSingleTextField
  category: FormReducerSelectField
  brand: FormReducerSingleTextField
  reference_image_url: FormReducerSingleTextField

  //   core data
  product_name: FormReducerSingleTextField
  product_types: FormReducerSingleTextField
  range: FormReducerSingleTextField
  product_description: FormReducerSingleTextField
  product_features: FormReducerSingleTextField
  instructions: FormReducerSingleTextField

  //   share attributes
  quantity: FormReducerSingleTextField
  size: FormReducerSingleTextField
  colour: FormReducerSingleTextField
  flavour: FormReducerSingleTextField
  format: FormReducerSingleTextField
  formulation: FormReducerSingleTextField
  product_dimensions: FormReducerSingleTextField
  pack_count: FormReducerSingleTextField
  model_number: FormReducerSingleTextField

  //   keywords
  priority: FormReducerMultiTextField
  required: FormReducerMultiTextField
  optional: FormReducerMultiTextField
  omit: FormReducerMultiTextField

  //   keyphrases
  keyphrases_required: FormReducerMultiTextField
  keyphrases_optional: FormReducerMultiTextField
}

type PickEditProductStateField<T> = PickByType<EditProductFormReducerState, T>

// Define the keys of the fields that match the correct element types
export type EditProductSingleTextField = PickEditProductStateField<FormReducerSingleTextField>
export type EditProductMultiTextField = PickEditProductStateField<FormReducerMultiTextField>

// Get the keys of the fields that have a value and an error field
export type EditProductValidationFields = BaseValidationField<EditProductFormReducerState>

export const defaultEditProductErrorObject: ValidationObject<'EditProductFormReducerState'> = {}
const withValidation = withValidationFactory(defaultEditProductErrorObject)

// Define the initial state of the form
export const initialEditProductFormReducerState = (data: ProductsDetailDataV5): EditProductFormReducerState => ({
  reference_name: withValidation(
    createFormField('text', data.reference_name || ''),
    'reference_name',
    ['requiredField'],
    [],
  ),
  product_id: withValidation(createFormField('text', data.product_id || ''), 'product_id', ['requiredField'], []),
  category: withValidation(createFormField('select', (data.gpc_code as any) || 0), 'category', [], ['requiredField']),
  brand: withValidation(
    createFormField('text', data.attributes?.find(({ name }) => name === 'brand')?.value || ''),
    'brand',
    ['requiredField'],
    [],
  ),
  reference_image_url: withValidation(
    createFormField('text', data.reference_image_url || ''),
    'reference_image_url',
    [],
    [],
  ),

  //   core data
  product_name: withValidation(
    createFormField('text', data.attributes?.find(({ name }) => name === 'product_name')?.value || ''),
    'product_name',
    ['requiredField'],
    [],
  ),
  product_types: withValidation(
    createFormField('text', data.attributes?.find(({ name }) => name === 'product_types')?.value || ''),
    'product_types',
    ['requiredField'],
    [],
  ),
  range: withValidation(
    createFormField('text', data.attributes?.find(({ name }) => name === 'range')?.value || ''),
    'range',
    [],
    [],
  ),

  product_description: withValidation(
    createFormField('text', data.attributes?.find(({ name }) => name === 'product_description')?.value || ''),
    'product_description',
    ['requiredField'],
    [],
  ),
  product_features: withValidation(
    createFormField('text', data.attributes?.find(({ name }) => name === 'product_features')?.value || ''),
    'product_features',
    [],
    [],
  ),
  instructions: withValidation(
    createFormField('text', data.attributes?.find(({ name }) => name === 'instructions')?.value || ''),
    'instructions',
    [],
    [],
  ),

  //   share attributes
  quantity: withValidation(
    createFormField('text', data.attributes?.find(({ name }) => name === 'quantity')?.value || ''),
    'quantity',
    [],
    [],
  ),
  size: withValidation(
    createFormField('text', data.attributes?.find(({ name }) => name === 'size')?.value || ''),
    'size',
    [],
    [],
  ),
  colour: withValidation(
    createFormField('text', data.attributes?.find(({ name }) => name === 'colour')?.value || ''),
    'colour',
    [],
    [],
  ),
  flavour: withValidation(
    createFormField('text', data.attributes?.find(({ name }) => name === 'flavour')?.value || ''),
    'flavour',
    [],
    [],
  ),
  format: withValidation(
    createFormField('text', data.attributes?.find(({ name }) => name === 'format')?.value || ''),
    'format',
    [],
    [],
  ),
  formulation: withValidation(
    createFormField('text', data.attributes?.find(({ name }) => name === 'formulation')?.value || ''),
    'formulation',
    [],
    [],
  ),
  product_dimensions: withValidation(
    createFormField('text', data.attributes?.find(({ name }) => name === 'product_dimensions')?.value || ''),
    'product_dimensions',
    [],
    [],
  ),
  pack_count: withValidation(
    createFormField('text', data.attributes?.find(({ name }) => name === 'pack_count')?.value || ''),
    'pack_count',
    [],
    [],
  ),
  model_number: withValidation(
    createFormField('text', data.attributes?.find(({ name }) => name === 'model_number')?.value || ''),
    'model_number',
    [],
    [],
  ),

  //   keywords
  priority: createFormField('multiText', data.keywords?.priority || []),
  required: createFormField('multiText', data.keywords?.required || []),
  optional: createFormField('multiText', data.keywords?.optional || []),
  omit: createFormField('multiText', data.keywords?.omit || []),
  //   keyphrases
  keyphrases_required: createFormField('multiText', data.keywords?.keyphrases_required || []),
  keyphrases_optional: createFormField('multiText', data.keywords?.keyphrases_optional || []),
})

const editProductFormReducer = formReducer<'EditProductFormReducerState'>
export const editProductFormHelperFunctions = formHelperMethodFactory<'EditProductFormReducerState'>()
export default editProductFormReducer
